/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2019, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'prop-types',
	'jquery'
], function(
	React,
	{ Container, Slideout },
	PropTypes,
	$
) {

	// TODO: use colors from ba-ui-toolkit essentials
	const BORDER_COLOR = '#eaeaea'; // $gray1
	const CONTRIBUTION_CLASS_NAME = 'slideout__contributions';

	const createContributionNode = (contributionId) => {
		const node = document.createElement('div');
		node.dataset.contributionId = contributionId;
		node.setAttribute('class', `${CONTRIBUTION_CLASS_NAME}__item`);
		node.style.overflow = 'auto';
		return node;
	};

	const contributionNodeMatchFn = (node, contributionId) => node.dataset.contributionId === contributionId;

	const findContributionNode = (parentNode, contributionId) => {
		const childNodes = Array.from(parentNode.childNodes);
		return childNodes.find((child) => contributionNodeMatchFn(child, contributionId));
	};

	/**
	 * Hide all contribution nodes, except the node that matches the current contribution id.
	 * The nodes representing the current contribution, should always be displayed, everything
	 * else should be hidden.
	 * @param {Node} parentNode - DOM element containing all the contributions
	 * @param {string} contributionId - current contribution id
	 */
	const hideContributionNodesExceptCurrent = (parentNode, contributionId) => {
		const childNodes = Array.from(parentNode.childNodes);
		childNodes.forEach((child) => {
			child.style.display = contributionNodeMatchFn(child, contributionId) ? '' : 'none';
		});
	};

	class InAppSlideoutContainer extends React.Component {

		constructor(props) {
			super(props);
			this.contentRef = React.createRef();
		}

		componentDidMount() {
			this._updateInAppSlideout();
		}

		componentWillUnmount () {
			this._onComponentMountedChange(/*isMounted*/false);
		}

		componentDidUpdate() {
			this._updateInAppSlideout();
		}

		_getContainerNode() {
			const { contribution } = this.props;
			const contributionId = contribution.getProviderId();

			const refNode = this.contentRef.current;
			if (!refNode) {
				// If something goes wrong and we can't get ahold of the content reference,
				// we will be unable to build a parent node
				return;
			}

			let resultNode = findContributionNode(refNode, contributionId);
			if (!resultNode) {
				resultNode = createContributionNode(contributionId);
				refNode.appendChild(resultNode);
			}

			return resultNode;
		}

		_updateInAppSlideout() {
			const {
				contribution,
				options
			} = this.props;

			if (!contribution) {
				return;
			}

			const contributionId = contribution.getProviderId();

			hideContributionNodesExceptCurrent(this.contentRef.current, contributionId);

			const containerNode = this._getContainerNode();
			if (!containerNode) {
				return;
			}

			contribution
				.render({
					...options,
					parentNode: containerNode
				})
				.then((node) => {
					if (!node) {
						return;
					}
					this.props._renderDone && this.props._renderDone(this);
					const childPanelId = node.dataset.childPanelId;
					if (options.isChild || childPanelId) {
						this._updateChildView(node, options);
					} else {
						this._updateParentView(node, containerNode);
					}
					this._onComponentMountedChange(/*isMounted*/true);
				});
		}

		_updateChildView(node, options = {}) {
			const { active } = this.props;

			if (active) {
				const containerNode = this._getContainerNode();
				let firstChild = containerNode.firstElementChild;
				if(firstChild) {
					if (containerNode.childNodes.length > 1) {
						// Canvas Tab properties is the only in R7 that uses refreshChild flag
						if (options.refreshChild) {
							// if is refreshing, then remove old child
							// using jquery remove can also clean up any attached handlers
							$(firstChild).remove();
							firstChild = containerNode.firstElementChild;
						}
					} else {
						firstChild.style.display = 'none';
					}
					containerNode.insertBefore(node, firstChild);
					containerNode.childNodes[0].focus();
				}
			}
		}

		_updateParentView(node, containerNode) {
			const { active } = this.props;

			if (active) {
				if (containerNode) {
					containerNode.innerHTML = '';
					containerNode.insertBefore(node, containerNode.firstElementChild);
					containerNode.childNodes[0].focus();
				}
			}
		}

		/**
		 * Invoke registered views when the InAppSlideoutContainer component get mount/unmounted in the DOM
		 *
		 * @param {booean} isMounted -isMounted boolean flag
		 */
		_onComponentMountedChange (isMounted) {
			const { onDomStateChange, contribution } = this.props;
			if (contribution && contribution.getProviderId && onDomStateChange) {
				onDomStateChange({
					type: isMounted ? 'mounted' : 'unmounted',
					current: contribution.getProviderId()
				});
			}
		}

		render() {
			const { active } = this.props;
			return (
				<Container relative height='100%' width='100%'>
					<Slideout
						animationOnRender={true}
						toDirection=''
						active={active}
						width='100%'
						style={{
							borderLeft: `1px solid ${BORDER_COLOR}`
						}}
					>
						<div ref={this.contentRef} className={CONTRIBUTION_CLASS_NAME}></div>
					</Slideout>
				</Container>
			);
		}
	}

	InAppSlideoutContainer.propTypes = {
		active: PropTypes.bool,
		contribution: PropTypes.object.isRequired,
		options: PropTypes.object,
		onDomStateChange: PropTypes.func,
		_renderDone: PropTypes.func //only used for testing
	};

	InAppSlideoutContainer.defaultProps = {
		active: false,
		options: {},
		onDomStateChange: () => {}
	};

	return InAppSlideoutContainer;
});
